import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  Chip,
} from "@mui/material";

import * as React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import Footer from "../footer/Footer";
import ParentLoginDialog from "../../pages/ParentLogin";
import { verifyTokenApi } from "../../api/parentApi";
import { useNavigate } from "react-router-dom";
import { CONFIG } from "../../api/apiClient";

const drawerWidth = 300;

const navItems = [
  { title: "Home", path: "/" },
  { title: "About", path: "/about" },
  { title: "Contact Us", path: "/contact" },
  { title: "Pricing", path: "/pricing" },
];

function DrawerAppBar(props) {
  const { window: windowProp } = props;
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [loginAnchor, setLoginAnchor] = React.useState(null);
  const [profileAnchor, setProfileAnchor] = React.useState(null);
  const [parentInfo, setParentInfo] = React.useState(null);
  const [openLogin, setOpenLogin] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  const openLoginMenu = Boolean(loginAnchor);
  const openProfileMenu = Boolean(profileAnchor);

  // Detect scroll for appbar shadow
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleDrawerToggle = () => setMobileOpen((v) => !v);
  const handleLoginMenuOpen = (e) => setLoginAnchor(e.currentTarget);
  const handleLoginMenuClose = () => setLoginAnchor(null);
  const handleProfileMenuOpen = (e) => setProfileAnchor(e.currentTarget);
  const handleProfileMenuClose = () => setProfileAnchor(null);

  const handleLogout = () => {
    localStorage.removeItem("parentInfo");
    localStorage.removeItem("parentToken");
    setParentInfo(null);
    handleProfileMenuClose();
  };

  React.useEffect(() => {
    const updateUser = () => {
      const storedParent = localStorage.getItem("parentInfo");
      setParentInfo(storedParent ? JSON.parse(storedParent) : null);
    };
    const handleOpenLogin = () => setOpenLogin(true);

    updateUser();
    window.addEventListener("userUpdated", updateUser);
    window.addEventListener("openLoginDialog", handleOpenLogin);
    return () => {
      window.removeEventListener("userUpdated", updateUser);
      window.removeEventListener("openLoginDialog", handleOpenLogin);
    };
  }, []);

  const container =
    typeof windowProp === "function"
      ? () => windowProp().document.body
      : windowProp?.document?.body;

  /* ---------- MOBILE DRAWER ---------- */
  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#0D1B4B" }}>
      {/* Drawer header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.5,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Box component={Link} to="/" onClick={() => setMobileOpen(false)}
          sx={{ display: "flex", alignItems: "center", gap: 1.5, textDecoration: "none" }}>
          <img src="/logo.png" alt="logo" style={{ height: 34, filter: "brightness(0) invert(1)" }} />
          <Typography fontWeight={700} fontSize="1.1rem" color="#fff">
            MySchoolling
          </Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle} sx={{ color: "#94a3b8" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Nav links */}
      <List sx={{ px: 2, pt: 2, flex: 1 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.title} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                sx={{
                  borderRadius: "12px",
                  py: 1.5,
                  px: 2.5,
                  bgcolor: isActive ? "rgba(79,110,247,0.2)" : "transparent",
                  border: isActive ? "1px solid rgba(79,110,247,0.35)" : "1px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.06)",
                  },
                }}
              >
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? "#fff" : "#94a3b8",
                    fontSize: "0.95rem",
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Auth section in drawer */}
      <Box sx={{ px: 2, pb: 4, borderTop: "1px solid rgba(255,255,255,0.08)", pt: 2 }}>
        {!parentInfo ? (
          <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => { setMobileOpen(false); setOpenLogin(true); }}
              sx={{
                borderRadius: "12px",
                py: 1.4,
                background: "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)",
                fontWeight: 600,
              }}
            >
              Login as Parent
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => { setMobileOpen(false); window.location.href = CONFIG.API_BASE_URL; }}
              sx={{
                borderRadius: "12px",
                py: 1.4,
                borderColor: "rgba(255,255,255,0.2)",
                color: "#94a3b8",
                fontWeight: 600,
                "&:hover": { borderColor: "rgba(255,255,255,0.4)", bgcolor: "rgba(255,255,255,0.04)" },
              }}
            >
              Login as School
            </Button>
          </Box>
        ) : (
          <Box mt={1}>
            <Box sx={{ p: 2, borderRadius: "14px", bgcolor: "rgba(79,110,247,0.12)", mb: 1.5 }}>
              <Typography fontWeight={700} color="#fff" fontSize="0.95rem">{parentInfo.name}</Typography>
              <Typography variant="caption" color="#94a3b8">{parentInfo.email}</Typography>
              <Box mt={1}>
                <Chip
                  label={parentInfo?.planName || "Free Plan"}
                  size="small"
                  sx={{ bgcolor: "rgba(79,110,247,0.25)", color: "#a5b4fc", fontWeight: 600, fontSize: "0.75rem" }}
                />
              </Box>
            </Box>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => { setMobileOpen(false); handleLogout(); }}
              sx={{
                borderRadius: "12px",
                py: 1.2,
                borderColor: "rgba(239,68,68,0.4)",
                color: "#f87171",
                fontWeight: 600,
                "&:hover": { borderColor: "#ef4444", bgcolor: "rgba(239,68,68,0.08)" },
              }}
            >
              Logout
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#F8FAFF" }}>
      <CssBaseline />

      {/* ===== APPBAR ===== */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: scrolled ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.85)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: scrolled
            ? "1px solid rgba(13,27,75,0.08)"
            : "1px solid rgba(13,27,75,0.04)",
          boxShadow: scrolled ? "0 4px 24px rgba(13,27,75,0.06)" : "none",
          transition: "all 0.3s ease",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            height: 72,
            px: { xs: 2, sm: 3, md: 5 },
            maxWidth: 1360,
            width: "100%",
            mx: "auto",
            display: "flex",
            alignItems: "center",
          }}
        >
          {/* LEFT: hamburger + logo */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                display: { md: "none" },
                color: "#0D1B4B",
                bgcolor: "rgba(13,27,75,0.05)",
                borderRadius: "10px",
                "&:hover": { bgcolor: "rgba(13,27,75,0.09)" },
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box
              component={Link}
              to="/"
              sx={{ display: "flex", alignItems: "center", gap: 1.5, textDecoration: "none" }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img src="/logo.png" alt="logo" style={{ height: 36, width: 36, objectFit: "cover" }} />
              </Box>
              <Typography
                fontWeight={800}
                fontSize="1.15rem"
                sx={{
                  background: "linear-gradient(135deg, #0D1B4B 0%, #4F6EF7 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                MySchoolling
              </Typography>
            </Box>
          </Box>

          {/* RIGHT: nav + auth */}
          <Box
            sx={{
              ml: "auto",
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Box
                  key={item.title}
                  component={Link}
                  to={item.path}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: "10px",
                    textDecoration: "none",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: "0.9rem",
                    color: isActive ? "#1A2F6E" : "#64748B",
                    bgcolor: isActive ? "rgba(79,110,247,0.08)" : "transparent",
                    border: isActive ? "1px solid rgba(79,110,247,0.18)" : "1px solid transparent",
                    position: "relative",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#1A2F6E",
                      bgcolor: "rgba(13,27,75,0.05)",
                    },
                  }}
                >
                  {item.title}
                </Box>
              );
            })}

            <Box sx={{ ml: 2 }}>
              {!parentInfo ? (
                <>
                  <Button
                    variant="contained"
                    onClick={handleLoginMenuOpen}
                    endIcon={<KeyboardArrowDownIcon />}
                    sx={{
                      borderRadius: "50px",
                      px: 3,
                      py: 1,
                      fontSize: "0.88rem",
                      fontWeight: 600,
                    }}
                  >
                    Login
                  </Button>

                  <Menu
                    anchorEl={loginAnchor}
                    open={openLoginMenu}
                    onClose={handleLoginMenuClose}
                    PaperProps={{
                      sx: {
                        mt: 1.5,
                        minWidth: 200,
                        borderRadius: "16px",
                        border: "1px solid rgba(13,27,75,0.08)",
                        boxShadow: "0 16px 48px rgba(13,27,75,0.14)",
                        overflow: "hidden",
                        p: 0.5,
                      },
                    }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  >
                    <MenuItem
                      onClick={() => { handleLoginMenuClose(); setOpenLogin(true); }}
                      sx={{ borderRadius: "10px", py: 1.2, fontWeight: 500, fontSize: "0.9rem", "&:hover": { bgcolor: "rgba(79,110,247,0.07)" } }}
                    >
                      Continue as Parent
                    </MenuItem>
                    <MenuItem
                      onClick={() => { handleLoginMenuClose(); window.location.href = CONFIG.API_BASE_URL; }}
                      sx={{ borderRadius: "10px", py: 1.2, fontWeight: 500, fontSize: "0.9rem", "&:hover": { bgcolor: "rgba(79,110,247,0.07)" } }}
                    >
                      Continue as School
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  <Box
                    onClick={handleProfileMenuOpen}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 0.8,
                      borderRadius: "50px",
                      border: "1.5px solid rgba(13,27,75,0.1)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": { bgcolor: "rgba(13,27,75,0.04)", borderColor: "rgba(79,110,247,0.3)" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        background: "linear-gradient(135deg, #1A2F6E 0%, #4F6EF7 100%)",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                      }}
                    >
                      {parentInfo.name?.charAt(0)}
                    </Avatar>
                    <Typography fontSize="0.88rem" fontWeight={600} color="#0D1B4B">
                      {parentInfo.name?.split(" ")[0]}
                    </Typography>
                    <KeyboardArrowDownIcon sx={{ fontSize: 16, color: "#64748b" }} />
                  </Box>

                  <Menu
                    anchorEl={profileAnchor}
                    open={openProfileMenu}
                    onClose={handleProfileMenuClose}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        mt: 1.5,
                        minWidth: 270,
                        borderRadius: "18px",
                        border: "1px solid rgba(13,27,75,0.08)",
                        boxShadow: "0 20px 60px rgba(13,27,75,0.15)",
                        overflow: "hidden",
                      },
                    }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  >
                    {/* Profile header */}
                    <Box
                      sx={{
                        px: 2.5,
                        py: 2.5,
                        background: "linear-gradient(135deg, #0D1B4B 0%, #1A2F6E 100%)",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                        <Avatar
                          sx={{
                            width: 42,
                            height: 42,
                            background: "rgba(255,255,255,0.15)",
                            border: "2px solid rgba(255,255,255,0.25)",
                            fontSize: "1rem",
                            fontWeight: 700,
                            color: "#fff",
                          }}
                        >
                          {parentInfo.name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={700} fontSize="0.95rem" color="#fff">
                            {parentInfo.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                            {parentInfo.email}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={parentInfo?.planName || "Free Plan"}
                        size="small"
                        sx={{
                          bgcolor: "rgba(79,110,247,0.3)",
                          color: "#a5b4fc",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          height: 24,
                        }}
                      />
                    </Box>

                    <Box sx={{ p: 1 }}>
                      <Box sx={{ px: 1.5, py: 1.5 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          sx={{
                            borderRadius: "10px",
                            py: 1,
                            fontWeight: 600,
                            fontSize: "0.85rem",
                          }}
                          onClick={() => { handleProfileMenuClose(); window.location.href = "/pricing#plans"; }}
                        >
                          Upgrade Plan
                        </Button>
                      </Box>

                      <Divider sx={{ mx: 1, my: 0.5 }} />

                      <MenuItem
                        onClick={handleLogout}
                        sx={{
                          borderRadius: "10px",
                          mx: 0.5,
                          py: 1.2,
                          color: "#ef4444",
                          fontWeight: 500,
                          fontSize: "0.9rem",
                          "&:hover": { bgcolor: "rgba(239,68,68,0.07)" },
                        }}
                      >
                        Logout
                      </MenuItem>
                    </Box>
                  </Menu>
                </>
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* MOBILE DRAWER */}
      <Drawer
        container={container}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        transitionDuration={280}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            bgcolor: "#0D1B4B",
            border: "none",
            boxShadow: "8px 0 40px rgba(13,27,75,0.25)",
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* MAIN CONTENT */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>

      <Footer />

      <ParentLoginDialog
        open={openLogin}
        handleClose={() => {
          setOpenLogin(false);
          const storedParent = localStorage.getItem("parentInfo");
          if (storedParent) setParentInfo(JSON.parse(storedParent));
        }}
      />
    </Box>
  );
}

export default DrawerAppBar;
